import { useRef } from "react";
import { clearModal } from "../../../../common-deprecated/redux/actions/ModalActions";
import { useCommonLabel } from "../../../../common-deprecated/utils/commonLabels";
import { useGradeSelectorDispatch, useGradeSelectorSelector } from "../redux/hooks";
import {
    type GradeSelectorFiltersType,
    clearFilterValue,
    addMultipleChoiceFilterValue,
    addFilterValue,
    clearFilters,
    addPriceFilterValue,
} from "../redux/slices/GradeSelectorFiltersSlice";
import { GradeSelectorFilterId, isMultipleChoiceFilter } from "../utils/filterConstants";
import { useFilteredGradeBodyTypes } from "../utils/filterHooks";
import {
    type FilterDataType,
    type FilterDataToRenderType,
    getResultButtonLabel,
    prepareCombinedFilterData,
    hasActiveFilters as checkActiveFilters,
} from "../utils/filterUtils";
import { MODAL_MOBILE_GRADE_SELECTOR_FILTER } from "../utils/modalConstants";
import type { GradeSelectorLabelType } from "../utils/labels";
import type { GradeBodyTypeType, PriceRangeType } from "../utils/types/Model";
import { getAemPriceConfig } from "../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { AemFmComponent } from "../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";

type UseMobileFilterModalType = {
    closeLabel: string;
    isResultButtonDisabled: boolean;
    resultButtonLabel: GradeSelectorLabelType;
    filters: FilterDataToRenderType[];
    handleMultipleChoiceInput: (filterId: GradeSelectorFilterId, code: string) => void;
    handleRadioInput: (filterId: GradeSelectorFilterId, code: string) => void;
    handlePriceInput: (filterId: GradeSelectorFilterId, price: PriceRangeType) => void;
    isFilterDisabled: (filterId: GradeSelectorFilterId, filterData: FilterDataType) => boolean;
    onClose: () => void;
    confirmSelection: () => void;
    activeFilters: GradeSelectorFiltersType["filters"];
    gradeBodyTypes: GradeBodyTypeType[];
};

const useMobileFilterModal = (): UseMobileFilterModalType => {
    const dispatch = useGradeSelectorDispatch();

    const gradeBodyTypes = useFilteredGradeBodyTypes();
    const allGradeBodyTypes = useGradeSelectorSelector((state) => state.gradeSelectorModel.model.gradeBodyTypes);
    const activeFilters = useGradeSelectorSelector((state) => state.gradeSelectorFilters.filters);
    const commonSettings = useGradeSelectorSelector((state) => state.commonSettings);
    const gradeSelectorSettings = useGradeSelectorSelector((state) => state.gradeSelectorSettings);
    const initialFilterState = useRef<GradeSelectorFiltersType["filters"] | undefined>(activeFilters);
    const modelCode = useGradeSelectorSelector((state) => state.gradeSelectorModel.model.code);
    const priceConfig = getAemPriceConfig(commonSettings, AemFmComponent.GradeSelector, modelCode);

    const closeLabel = useCommonLabel("close");

    const isResultButtonDisabled =
        Object.keys(activeFilters).length === 0 && initialFilterState.current
            ? Object.keys(initialFilterState.current).length === 0
            : false;

    const resultButtonLabel = getResultButtonLabel(isResultButtonDisabled, gradeBodyTypes.length);

    // Prepare filter data to be rendered.
    const filters = prepareCombinedFilterData(
        gradeBodyTypes,
        allGradeBodyTypes,
        activeFilters,
        commonSettings,
        gradeSelectorSettings,
        priceConfig,
    );

    const handleMultipleChoiceInput = (filterId: GradeSelectorFilterId, code: string): void => {
        if (isMultipleChoiceFilter(filterId)) {
            if (activeFilters[filterId]?.includes(code)) {
                if (activeFilters[filterId].length === 1) {
                    dispatch(clearFilterValue({ filterId }));
                } else
                    dispatch(
                        addMultipleChoiceFilterValue({
                            filterId,
                            value: activeFilters[filterId].filter((codeToRemove) => codeToRemove !== code),
                        }),
                    );
            } else {
                dispatch(
                    addMultipleChoiceFilterValue({
                        filterId,
                        value: activeFilters[filterId] ? [...activeFilters[filterId], code] : [code],
                    }),
                );
            }
        }
    };

    const isValidFilterIdForAddAction = (
        filterId: GradeSelectorFilterId,
    ): filterId is Exclude<
        GradeSelectorFilterId,
        GradeSelectorFilterId.Colour | GradeSelectorFilterId.Price | GradeSelectorFilterId.PopularFeatures
    > => {
        return !isMultipleChoiceFilter(filterId) && filterId !== GradeSelectorFilterId.Price;
    };

    const handleRadioInput = (filterId: GradeSelectorFilterId, code: string): void => {
        if (isValidFilterIdForAddAction(filterId)) {
            if (activeFilters[filterId] === code) {
                dispatch(clearFilterValue({ filterId }));
            } else {
                dispatch(addFilterValue({ filterId, value: code }));
            }
        }
    };

    const handlePriceInput = (filterId: GradeSelectorFilterId, price: PriceRangeType): void => {
        if (filterId === GradeSelectorFilterId.Price) {
            if (activeFilters[filterId] === price) {
                dispatch(clearFilterValue({ filterId }));
            } else {
                dispatch(addPriceFilterValue({ filterId, value: price }));
            }
        }
    };

    const isFilterDisabled = (filterId: GradeSelectorFilterId, filterData: FilterDataType): boolean => {
        if (filterId !== GradeSelectorFilterId.Price) {
            return (
                !activeFilters[filterId]?.includes(filterData.code) && filterData.uniqueGradeBodyTypeIds.length === 0
            );
        }
        return false;
    };

    // OnClose needs to reset the filter values to their initial state.
    const onClose = (): void => {
        const hasActiveFilters = checkActiveFilters(initialFilterState.current);

        if (hasActiveFilters) {
            Object.values(GradeSelectorFilterId).forEach((filterId) => {
                const filter = initialFilterState.current?.[filterId];
                if (filter) {
                    if (Array.isArray(filter)) {
                        dispatch(
                            addMultipleChoiceFilterValue({
                                filterId:
                                    filterId === GradeSelectorFilterId.PopularFeatures
                                        ? GradeSelectorFilterId.PopularFeatures
                                        : GradeSelectorFilterId.Colour,
                                value: filter,
                            }),
                        );
                    } else if (isValidFilterIdForAddAction(filterId) && typeof filter === "string") {
                        dispatch(addFilterValue({ filterId, value: filter }));
                    } else if (filterId === GradeSelectorFilterId.Price && typeof filter === "object") {
                        dispatch(addPriceFilterValue({ filterId, value: filter }));
                    }
                } else {
                    dispatch(clearFilterValue({ filterId }));
                }
            });
        } else {
            dispatch(clearFilters());
        }

        dispatch(clearModal(MODAL_MOBILE_GRADE_SELECTOR_FILTER));
    };

    const confirmSelection = (): void => {
        dispatch(clearModal(MODAL_MOBILE_GRADE_SELECTOR_FILTER));
    };

    return {
        closeLabel,
        filters,
        handleMultipleChoiceInput,
        handleRadioInput,
        handlePriceInput,
        isFilterDisabled,
        isResultButtonDisabled,
        onClose,
        resultButtonLabel,
        activeFilters,
        confirmSelection,
        gradeBodyTypes,
    };
};

export default useMobileFilterModal;
